import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Button, Form, DatePicker, ButtonToolbar, Message, Loader, Input, Panel, List, Tag, Divider, Drawer } from 'rsuite'
import SelectCourierPicker from '@components/common/commonFormInput/SelectCourierPicker'
import { RSUITE_DATEPICKER_LOCALE } from '@@partner:config/rsuite'
import { CourierWorkShift } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import { useMutateUpdateCourierWorkShift } from '@@partner:graphql/mutation/courier/updateCourierWorkShift.gql'
import {
  getLastShiftStartTime,
  getLastShiftEndTime,
  saveLastShiftStartTime,
  saveLastShiftEndTime,
  combineDateAndTime
} from '@@partner:helpers/shifts'
import SelectVehiclePicker from '@components/common/commonFormInput/SelectVehiclePicker'
import FormCheckbox from '@components/common/form/FormCheckbox'
import { useFetchSingleCourier } from '@@partner:graphql/query/employees/singleCourier.gql'
import { formatWorkshiftStatusLabel } from '@@partner:helpers/formatters/workshift'

export type ShiftFormData = {
  courierId?: GQL_ID
  startDate?: Date
  startTime?: string
  endDate?: Date
  endTime?: string
  vehicleId?: GQL_ID
  is_confirmation_required?: boolean
}

export type ShiftModalMode = 'create' | 'edit'

type ShiftModalProps = {
  mode?: ShiftModalMode
  isOpen: boolean
  isLoading?: boolean
  shift?: CourierWorkShift | null
  initialDate?: Date | null
  onClose: () => void
  onSave?: () => void
  onEdit?: (shift: CourierWorkShift) => void // Новый проп для перехода в режим редактирования
}

const defaultFormValue: ShiftFormData = {
  courierId: undefined,
  startDate: new Date(),
  startTime: getLastShiftStartTime(),
  endDate: new Date(),
  endTime: getLastShiftEndTime(),
  vehicleId: undefined
}

// Регулярное выражение для проверки формата времени (HH:MM)
const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

export default function ShiftModal ({
  mode = 'create',
  isOpen,
  isLoading,
  shift,
  initialDate,
  onClose,
  onSave,
  onEdit
}: ShiftModalProps) {
  const [ formValue, setFormValue ] = React.useState<ShiftFormData>(defaultFormValue)
  const [ formError, setFormError ] = useState<Error | null>(null)

  const [ updateWorkshift, { loading, error } ] = useMutateUpdateCourierWorkShift()

  // Функции для форматирования данных
  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return {
      date: date.toLocaleDateString('ru-RU'),
      time: date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    }
  }

  const { data: courierData, loading: loadingCourier, error: errorCourier } = useFetchSingleCourier({
    id: formValue.courierId
  })

  useEffect(() => {
    if (courierData?.courier.id) {
      setFormValue(prev => ({
        ...prev,
        is_confirmation_required: courierData.courier.is_shift_require_confirmation
      }))
    }
  }, [ courierData ])

  // При открытии модалки с существующей сменой
  useEffect(() => {
    if (shift) {
      const startDate = new Date(shift.plannedStartAt)
      const endDate = new Date(shift.plannedFinishAt)
      
      setFormValue({
        courierId: shift.courier_id,
        startDate: startDate,
        startTime: startDate.getHours().toString().padStart(2, '0') + ':' + 
                  startDate.getMinutes().toString().padStart(2, '0'),
        endDate: endDate,
        endTime: endDate.getHours().toString().padStart(2, '0') + ':' + 
                endDate.getMinutes().toString().padStart(2, '0'),
        vehicleId: shift.vehicle_id,
        is_confirmation_required: shift.courier?.is_shift_require_confirmation || false
      })
    }
    else {
      const baseFormValue = { ...defaultFormValue }
      
      // Если передана начальная дата, используем её
      if (initialDate) {
        baseFormValue.startDate = initialDate
        baseFormValue.endDate = initialDate
      }
      
      setFormValue(baseFormValue)
    }
  }, [ shift, initialDate ])
  
  // Функция для проверки корректности формата времени
  const validateTime = (time?: string): boolean => {
    if (!time) return false
    return timePattern.test(time)
  }
  
  // Обработчик изменения времени
  const handleTimeChange = (field: 'startTime' | 'endTime') => (value: string) => {
    if (!value || timePattern.test(value)) {
      setFormValue(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = useCallback(async () => {
    if (!formValue.courierId || !formValue.startDate || !formValue.endDate || 
        !validateTime(formValue.startTime) || !validateTime(formValue.endTime)) {
      setFormError(new Error('Пожалуйста, заполните все обязательные поля'))
      return
    }

    try {
      // Сохраняем время в localStorage для следующего использования
      if (formValue.startTime) saveLastShiftStartTime(formValue.startTime)
      if (formValue.endTime) saveLastShiftEndTime(formValue.endTime)

      // Комбинируем дату и время
      const plannedStartAt = combineDateAndTime(formValue.startDate, formValue.startTime!)
      const plannedFinishAt = combineDateAndTime(formValue.endDate, formValue.endTime!)
      
      // Проверяем, что время окончания больше времени начала
      if (plannedFinishAt <= plannedStartAt) {
        setFormError(new Error('Время окончания должно быть больше времени начала'))
        return
      }
      
      await updateWorkshift({
        variables: {
          upsert: !shift,
          newData: {
            ...(shift?.id && { id: shift.id }),
            courier_id: formValue.courierId,
            plannedStartAt: plannedStartAt.getTime().toString(),
            plannedFinishAt: plannedFinishAt.getTime().toString(),
            vehicle_id: formValue.vehicleId
          }
        }
      })
      setFormError(null)
      if (onSave) { 
        onSave() 
      }
      onClose()
    } 
    catch (error) {
      setFormError(error as Error)
      console.error('Failed to save shift:', error)
    }
  }, [ formValue, shift, onClose, onSave, updateWorkshift ])

  // Компонент для просмотра деталей смены
  const ShiftViewContent = () => {
    if (!shift) return null

    const startTime = formatDateTime(Number(shift.plannedStartAt))
    const endTime = formatDateTime(Number(shift.plannedFinishAt))
    const statusInfo = formatWorkshiftStatusLabel(shift.status)
    
    const actualStartTime = shift.startedAt ? formatDateTime(Number(shift.startedAt)) : null
    const actualEndTime = shift.finishedAt ? formatDateTime(Number(shift.finishedAt)) : null

    return (
      <div style={{ padding: '8px 0' }}>
        {/* Основная информация о смене */}
        <Panel header="📋 Информация о смене" bordered style={{ marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Курьер:</div>
                <div style={{ fontWeight: '600' }}>
                  {shift.courier ? `${shift.courier.first_name} ${shift.courier.last_name}` : 'Не назначен'}
                </div>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Статус:</div>
                <Tag color={statusInfo.color as any}>{statusInfo.label}</Tag>
              </div>
            </div>
            
            <div>
              {shift.vehicle && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Транспорт:</div>
                  <div>{shift.vehicle.brand} {shift.vehicle.model}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {shift.vehicle.license_plate}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Panel>

        {/* Время смены */}
        <Panel header="⏰ Время смены" bordered style={{ marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Запланировано</div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Начало:</strong> {startTime.date} в {startTime.time}
              </div>
              <div>
                <strong>Окончание:</strong> {endTime.date} в {endTime.time}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Фактически</div>
              {actualStartTime ? (
                <div style={{ marginBottom: '4px' }}>
                  <strong>Начато:</strong> {actualStartTime.date} в {actualStartTime.time}
                </div>
              ) : (
                <div style={{ marginBottom: '4px', color: '#666' }}>Не начата</div>
              )}
              
              {actualEndTime ? (
                <div>
                  <strong>Завершено:</strong> {actualEndTime.date} в {actualEndTime.time}
                </div>
              ) : (
                <div style={{ color: '#666' }}>Не завершена</div>
              )}
            </div>
          </div>
        </Panel>

        {/* Магазины курьера */}
        {shift.courier?.shops && shift.courier.shops.length > 0 && (
          <Panel header="🏪 Магазины" bordered style={{ marginBottom: '16px' }}>
            <List size="sm">
              {shift.courier.shops.map((shop) => (
                <List.Item key={shop.id}>
                  <div>
                    <div style={{ fontWeight: '600' }}>{shop.name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{shop.address}</div>
                  </div>
                </List.Item>
              ))}
            </List>
          </Panel>
        )}

        {/* Заказы - пока заглушка, потом добавим реальные данные */}
        <Panel header="📦 Заказы" bordered>
          <div style={{ 
            textAlign: 'center', 
            padding: '24px', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            Информация о заказах будет добавлена в следующих обновлениях
          </div>
        </Panel>
      </div>
    )
  }

  return (
    <Modal open={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>
          {mode === 'edit' && 'Редактирование смены'}
          {mode === 'create' && 'Создание смены'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(formError || error) && (
          <Message type="error" className="u-mb-md">
            {formError ? formError.message : error?.message}
          </Message>
        )}
        
        <Form
          fluid
          formValue={formValue}
          onChange={setFormValue}
          onSubmit={handleSubmit}
        >
          <Form.Group>
            <Form.ControlLabel>Курьер</Form.ControlLabel>
            <Form.Control
              name="courierId"
              accepter={SelectCourierPicker}
            />
          </Form.Group>

          {/* Информационный блок с магазинами и зонами курьера */}
          {formValue.courierId && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '16px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#495057' }}>
                Информация о курьере
              </div>
              
              {loadingCourier && (
                <div style={{ textAlign: 'center', padding: '8px' }}>
                  <Loader size="sm" content="Загрузка информации о курьере..." />
                </div>
              )}

              {errorCourier && (
                <div style={{ fontSize: '12px', color: '#dc3545', fontStyle: 'italic' }}>
                  Ошибка загрузки информации о курьере
                </div>
              )}

              {courierData?.courier && !loadingCourier && (
                <>
                  {courierData.courier.shops && courierData.courier.shops.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', color: '#6c757d', marginBottom: '4px' }}>
                        Магазины ({courierData.courier.shops.length}):
                      </div>
                      <div style={{ fontSize: '12px', color: '#495057' }}>
                        {courierData.courier.shops.map((shop, index) => (
                          <div key={shop.id} style={{ marginBottom: '2px' }}>
                            • {shop.name} - {shop.address}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {courierData.courier.deliveryZones && courierData.courier.deliveryZones.length > 0 && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '500', color: '#6c757d', marginBottom: '4px' }}>
                        Зоны доставки ({courierData.courier.deliveryZones.length}):
                      </div>
                      <div style={{ fontSize: '12px', color: '#495057' }}>
                        {courierData.courier.deliveryZones.map((zone, index) => (
                          <div key={zone.id} style={{ marginBottom: '2px' }}>
                            • {zone.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!courierData.courier.shops?.length && !courierData.courier.deliveryZones?.length) && (
                    <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                      У курьера не назначены магазины и зоны доставки
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <Form.Group>
            <Form.ControlLabel>Транспорт</Form.ControlLabel>
            <Form.Control
              name="vehicleId"
              accepter={SelectVehiclePicker}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Дата и время начала</Form.ControlLabel>
            <div className="u-flex">
              <div style={{ width: '60%', marginRight: 8 }}>
                <Form.Control
                  name="startDate"
                  oneTap
                  isoWeek
                  accepter={DatePicker}
                  format="dd.MM.yyyy"
                  block
                />
              </div>
              <div style={{ width: '40%' }}>
                <Input 
                  value={formValue.startTime}
                  onChange={handleTimeChange('startTime')}
                  placeholder="HH:MM"
                />
              </div>
            </div>
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Дата и время окончания</Form.ControlLabel>
            <div className="u-flex">
              <div style={{ width: '60%', marginRight: 8 }}>
                <Form.Control
                  name="endDate"
                  oneTap
                  isoWeek
                  accepter={DatePicker}
                  format="dd.MM.yyyy"
                  block
                />
              </div>
              <div style={{ width: '40%' }}>
                <Input 
                  value={formValue.endTime}
                  onChange={handleTimeChange('endTime')}
                  placeholder="HH:MM"
                />
              </div>
            </div>
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Требуется подтверждения завершения</Form.ControlLabel>
            <FormCheckbox.Control name='is_confirmation_required' />
          </Form.Group>
        </Form>

        {isLoading && <Loader center content="Сохранение..." />}
      </Modal.Body>

      <Modal.Footer>
        <ButtonToolbar>
          <Button appearance="subtle" onClick={onClose}>
            Отмена
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={isLoading}>
            {mode === 'edit' ? 'Сохранить' : 'Создать'}
          </Button>
        </ButtonToolbar>
      </Modal.Footer>
    </Modal>
  )
}
